import React, { useMemo, useState } from 'react';
import { COST_TYPES, DISCIPLINES, OPERATION_CODES } from '../../data/codingStructureData';
import { BrandMark } from './screens';
import { filterOperationCodes, groupOperationCodes } from './codingStructureHelpers';

const Back = ({ onClick }) => (
  <button type="button" className="lgl-m-back" onClick={onClick} aria-label="Back to menus">←</button>
);

export const CodingStructureScreen = ({ onBack }) => {
  const [tab, setTab] = useState('codes');
  const [query, setQuery] = useState('');
  const [selectedCode, setSelectedCode] = useState(null);
  const codes = useMemo(
    () => filterOperationCodes(OPERATION_CODES, query),
    [query]
  );
  const groups = useMemo(() => groupOperationCodes(codes), [codes]);

  return (
    <>
      <header className="lgl-m-top">
        <div className="lgl-m-navrow">
          <Back onClick={onBack} />
          <div>
            <p className="lgl-m-kicker">Accounting</p>
            <h1 className="lgl-m-title">Coding Structure</h1>
            <p className="lgl-m-sub">Codes, cost types, and disciplines</p>
          </div>
          <BrandMark />
        </div>
        <div className="lgl-m-tabs" role="tablist" aria-label="Coding structure views">
          {[
            ['codes', 'Codes'],
            ['cost-types', 'Cost types'],
            ['disciplines', 'Disciplines'],
          ].map(([id, label]) => (
            <button
              key={id}
              type="button"
              role="tab"
              aria-selected={tab === id}
              className={tab === id ? 'is-on' : ''}
              onClick={() => {
                setTab(id);
                setSelectedCode(null);
              }}
            >
              {label}
            </button>
          ))}
        </div>
        {tab === 'codes' ? (
          <input
            className="lgl-m-search"
            type="search"
            placeholder="Search operation code or description"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
        ) : null}
      </header>
      <main className="lgl-m-body">
        {tab === 'codes' && selectedCode ? (
          <article className="lgl-m-field lgl-m-code-detail">
            <div className="lgl-m-linehead">
              <div>
                <p className="lgl-m-kicker">Operation code</p>
                <h2 className="lgl-m-title">{selectedCode.code}</h2>
              </div>
              <button type="button" className="lgl-m-iconbtn" onClick={() => setSelectedCode(null)} aria-label="Close code detail">×</button>
            </div>
            <p className="lgl-m-code-description">{selectedCode.description}</p>
            <dl>
              <div><dt>Type</dt><dd>{selectedCode.type}</dd></div>
              <div><dt>Group</dt><dd>{selectedCode.group}</dd></div>
              <div><dt>Cost type</dt><dd>{selectedCode.costType || 'Not assigned'}</dd></div>
            </dl>
          </article>
        ) : null}
        {tab === 'codes' ? (
          <>
            <p className="lgl-m-section">{codes.length} operation codes</p>
            {groups.map(([group, groupCodes]) => (
              <section key={group}>
                <p className="lgl-m-section">{group}</p>
                {groupCodes.map((code) => (
                  <button key={code.code} type="button" className="lgl-m-card lgl-m-code-card" onClick={() => setSelectedCode(code)}>
                    <div className="lgl-m-linehead">
                      <strong>{code.code}</strong>
                      <span>{code.costType || '—'}</span>
                    </div>
                    <div className="lgl-m-sub">{code.description}</div>
                  </button>
                ))}
              </section>
            ))}
            {!codes.length ? <p className="lgl-m-empty">No operation codes match that search.</p> : null}
          </>
        ) : null}
        {tab === 'cost-types' ? Object.values(COST_TYPES).map((costType) => (
          <article className="lgl-m-card lgl-m-coding-card" key={costType.letter} style={{ '--lgl-module-accent': costType.color }}>
            <div className="lgl-m-linehead">
              <div className="lgl-m-jobname">{costType.letter} · {costType.name}</div>
            </div>
            <p className="lgl-m-sub">{costType.description}</p>
          </article>
        )) : null}
        {tab === 'disciplines' ? DISCIPLINES.map((discipline) => (
          <article className="lgl-m-card lgl-m-coding-card" key={discipline.id} style={{ '--lgl-module-accent': discipline.color }}>
            <div className="lgl-m-linehead">
              <div className="lgl-m-jobname">{discipline.id} · {discipline.name}</div>
            </div>
            <p className="lgl-m-sub">Labour {discipline.labourRange} · Extras {discipline.extrasCode}</p>
          </article>
        )) : null}
      </main>
    </>
  );
};

